import { admittedModalQueueAtom, seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import { useDocExportSuggestionBanner } from '@/components/ui/session-ask-heidi/doc-export-suggestion-banner/useDocExportSuggestionBanner';
import { useEvidenceSummarySuggestion } from '@/container/ScribeTabs/hooks/useEvidenceSummarySuggestion';
import { useIsSourceRecommendationPopoverBannerEnabled } from '@/features/flags';
import { useDocumentCreationSuggestionBanner } from '@/hooks/useDocumentCreationSuggestionBanner';
import { useSourceRecommendationBanner } from '@/hooks/useSourceRecommendationBanner';
import { StreamingStatus } from '@/models/scribeRecordModel';
import type { RootState } from '@/store';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { ModalId, RecordingStatus } from '@heidi/schema/ml-scribe';
import { activeSessionIdAtom, useQuerySessionById } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

/** Consult note / streaming lifecycle states where an in-progress transcription should hide competing promos. */
const ACTIVE_NOTE_STREAMING_STATUSES = new Set<StreamingStatus>([
  StreamingStatus.UPLOADING,
  StreamingStatus.AWAITING_STREAMING_START,
  StreamingStatus.STREAMING,
  StreamingStatus.FINISHING_STREAMING,
  StreamingStatus.CONVERTING_SECTIONAL_NOTES,
]);

export type SessionlessEvidenceAwarenessPresentationSuppression = {
  /** Any coexistence rule hides the popover (recording, banners, modal queue, etc.). */
  isSuppressed: boolean;
  /**
   * Recording, paused consult, timer running, or active consult-note streaming pipeline.
   * Subset of `isSuppressed` — used when the user starts transcription work while the
   * popover was visible (counts as dismiss for consecutive_dismiss streaks).
   */
  isTranscriptionWorkflowSuppressed: boolean;
};

/**
 * When `isSuppressed`, the sessionless Evidence awareness popover should not render, to avoid
 * stacking with recording/transcription work, queued modals, or other Evidence recommendation banners.
 */
export function useSessionlessEvidenceAwarenessPresentationSuppressed(): SessionlessEvidenceAwarenessPresentationSuppression {
  const routeSessionId = useCurrentSessionId();
  const activeRecordingSessionId = useAtomValue(activeSessionIdAtom);
  const effectiveSessionId = routeSessionId || activeRecordingSessionId || '';

  const isTimerRunning = useSelector((state: RootState) => state.timerModel.isRunning);

  const { data: sessionSlice } = useQuerySessionById({
    sessionId: effectiveSessionId,
    select: (data) =>
      data
        ? {
            recording_status: data.recording_status,
            streamingStatus: data.streamingStatus,
            consultNoteStatus: data.consult_note?.status,
          }
        : undefined,
  });

  const isRecordingOrPaused =
    isTimerRunning ||
    sessionSlice?.recording_status === RecordingStatus.RECORDING ||
    sessionSlice?.recording_status === RecordingStatus.PAUSED;

  const isNotePipelineActive = Boolean(
    sessionSlice?.streamingStatus != null &&
    ACTIVE_NOTE_STREAMING_STATUSES.has(sessionSlice.streamingStatus)
  );

  const { shouldShow: sourceRecommendationMainVisible } = useSourceRecommendationBanner(
    'chat',
    { trackImpressions: false }
  );

  const isSourcePopoverBannerEnabled = useIsSourceRecommendationPopoverBannerEnabled();
  const { shouldShow: sourceRecommendationPopoverEligible } =
    useSourceRecommendationBanner('popover', { trackImpressions: false });
  const seenModalIds = useAtomValue(seenModalIdsAtom);
  const modalQueue = useAtomValue(admittedModalQueueAtom);
  const isModalQueueNotEmpty = modalQueue.length > 0;
  const isSourceApplyBannerVisible = Boolean(
    isSourcePopoverBannerEnabled &&
    sourceRecommendationPopoverEligible &&
    (seenModalIds?.has(ModalId.SOURCE_RECOMMENDATION_BANNER) ?? false)
  );

  const { shouldShowBanner: documentSuggestionBannerVisible } =
    useDocumentCreationSuggestionBanner(routeSessionId || undefined, {
      trackImpressions: false,
    });

  const docExportBanner = useDocExportSuggestionBanner({ trackImpressions: false });
  const isDocExportBannerVisible = docExportBanner != null;

  const { shouldShow: evidenceSummarySuggestionVisible } = useEvidenceSummarySuggestion({
    sessionId: effectiveSessionId,
    consultNoteStatus: sessionSlice?.consultNoteStatus,
    streamingStatus: sessionSlice?.streamingStatus,
    trackImpressions: false,
  });

  const isTranscriptionWorkflowSuppressed = isRecordingOrPaused || isNotePipelineActive;

  const isSuppressed =
    isTranscriptionWorkflowSuppressed ||
    isModalQueueNotEmpty ||
    sourceRecommendationMainVisible ||
    isSourceApplyBannerVisible ||
    documentSuggestionBannerVisible ||
    isDocExportBannerVisible ||
    evidenceSummarySuggestionVisible;

  return { isSuppressed, isTranscriptionWorkflowSuppressed };
}
